import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { IAlternativeCodeInput } from 'src/app/@interfaces/alternative-code.interface';
import { IUnits } from 'src/app/@interfaces/units.interface';
import { UnitConversionService } from 'src/app/@shared/services/unit-conversion.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface AlternativeCodeDialogData {
  providers: any[];
  units: IUnits[];
  alternativeCode?: IAlternativeCodeInput;
  articlePackagingUnit?: IUnits;
  articleBaseUnit?: IUnits; // Base unit of the article for conversion validation
}

@Component({
  selector: 'app-alternative-code-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    TranslateModule,
  ],
  templateUrl: './alternative-code-dialog.component.html',
  styleUrls: ['./alternative-code-dialog.component.css'],
})
export class AlternativeCodeDialogComponent implements OnInit, OnDestroy {
  form: FormGroup;
  isFactorLocked = false;
  private unsubscribe$ = new Subject<void>();

  get calculatedFactor(): number {
    const qty = this.form.get('packaging_quantity')?.value || 1;
    const units = this.form.get('units_per_package')?.value || 1;
    return qty * units;
  }

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<AlternativeCodeDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AlternativeCodeDialogData,
    private unitConversionService: UnitConversionService,
  ) {
    this.form = this.fb.group({
      alternative_code: ['', [Validators.required]],
      id_provider: [null],
      id_unit: [null],
      packaging_quantity: [1, [Validators.min(0.01)]],
      units_per_package: [1, [Validators.min(0.01)]],
      description: [''],
    });
  }

  ngOnInit(): void {
    if (this.data.alternativeCode) {
      // Convert id values to ensure proper select binding
      const codeData = {
        alternative_code: this.data.alternativeCode.alternative_code,
        id_provider: this.data.alternativeCode.id_provider
          ? String(this.data.alternativeCode.id_provider)
          : null,
        id_unit: this.data.alternativeCode.id_unit
          ? String(this.data.alternativeCode.id_unit)
          : null,
        packaging_quantity: this.data.alternativeCode.packaging_quantity || 1,
        units_per_package: this.data.alternativeCode.units_per_package || 1,
        description: this.data.alternativeCode.description || '',
      };

      this.form.patchValue(codeData);
    }

    // Listen to unit changes to validate standard conversions
    this.form
      .get('id_unit')
      ?.valueChanges.pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.onUnitChange();
      });

    // Initial validation if unit is already set
    if (this.form.get('id_unit')?.value) {
      this.onUnitChange();
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  async onUnitChange(): Promise<void> {
    const altUnitId = this.form.get('id_unit')?.value;
    const baseUnitId = this.data.articleBaseUnit?.id_unit;

    // Reset factor locked state
    this.isFactorLocked = false;

    // If no alternative unit selected (same as article), or no base unit, allow custom factor
    if (!altUnitId || !baseUnitId) {
      this.form.get('packaging_quantity')?.enable();
      this.form.get('units_per_package')?.enable();
      return;
    }

    // Check if there's a standard conversion
    try {
      // For alternative code packaging, we need: how many base units in one alt unit?
      // So we ask for conversion FROM alt unit TO base (e.g., Caja -> Gramo)
      const hasConversion = await this.unitConversionService.hasConversion(
        Number(altUnitId), // FROM alternative unit
        Number(baseUnitId), // TO base unit
      );

      if (hasConversion) {
        // Get the conversion factor from database
        // This gives us: how many base units in one alternative unit
        const factor = await this.unitConversionService
          .getConversionFactor(Number(altUnitId), Number(baseUnitId))
          .toPromise();

        if (factor && factor > 0) {
          // Lock the factor and apply standard conversion
          this.isFactorLocked = true;
          this.form.patchValue({
            packaging_quantity: factor,
            units_per_package: 1,
          });

          // Disable the factor fields
          this.form.get('packaging_quantity')?.disable();
          this.form.get('units_per_package')?.disable();
        }
      } else {
        // No standard conversion, allow custom factor
        this.isFactorLocked = false;

        // Enable the factor fields for manual input
        this.form.get('packaging_quantity')?.enable();
        this.form.get('units_per_package')?.enable();
      }
    } catch (error) {
      console.error('Error checking unit conversion:', error);
      // On error, allow custom factor
      this.isFactorLocked = false;
      this.form.get('packaging_quantity')?.enable();
      this.form.get('units_per_package')?.enable();
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    if (this.form.valid) {
      const formValue = this.form.getRawValue(); // Use getRawValue to get disabled fields too

      // Find provider and unit objects from the lists to preserve nested data
      const selectedProvider = formValue.id_provider
        ? this.data.providers.find(
            (p) => String(p.id_provider) === String(formValue.id_provider),
          )
        : null;

      const selectedUnit = formValue.id_unit
        ? this.data.units.find(
            (u) => String(u.id_unit) === String(formValue.id_unit),
          )
        : null;

      const result: IAlternativeCodeInput = {
        alternative_code: formValue.alternative_code,
        id_provider: formValue.id_provider
          ? Number(formValue.id_provider)
          : undefined,
        id_unit: formValue.id_unit ? Number(formValue.id_unit) : undefined,
        packaging_quantity: Number(formValue.packaging_quantity) || 1,
        units_per_package: Number(formValue.units_per_package) || 1,
        conversion_factor: this.calculatedFactor,
        description: formValue.description || undefined,
        // Preserve nested objects for display in the table
        provider: selectedProvider || undefined,
        unit: selectedUnit || undefined,
      };

      this.dialogRef.close(result);
    }
  }
}
